import { useId, type InputHTMLAttributes, type ReactNode } from 'react';

import styles from './Field.module.css';

interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'aria-invalid'> {
  readonly label: string;
  /** Mensagens de erro do campo. Vazio significa válido. */
  readonly errors?: readonly string[] | undefined;
  /** Texto de apoio, sempre visível. */
  readonly hint?: ReactNode | undefined;
}

/**
 * Campo de formulário com rótulo, dica e erro amarrados por ARIA.
 *
 * <p>
 * O rótulo é <b>sempre</b> um <code>label</code> de verdade, nunca um
 * placeholder: placeholder some ao digitar, e quem tem dificuldade de memória
 * de trabalho perde a referência do que estava preenchendo (docs/22 §6).
 * </p>
 * <p>
 * O erro é anunciado por <code>aria-describedby</code> e por
 * <code>role="alert"</code>. Sem isso, o leitor de tela lê o campo como válido
 * e o usuário só descobre o problema ao tentar enviar de novo.
 * </p>
 */
export function Field({ label, errors = [], hint, ...input }: FieldProps): JSX.Element {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const hasError = errors.length > 0;

  const describedBy = [hint === undefined ? null : hintId, hasError ? errorId : null]
    .filter((value): value is string => value !== null)
    .join(' ');

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
        {input.required === true && (
          <span aria-hidden="true" className={styles.required}>
            {' *'}
          </span>
        )}
      </label>

      {hint !== undefined && (
        <p className={styles.hint} id={hintId}>
          {hint}
        </p>
      )}

      <input
        {...input}
        aria-describedby={describedBy === '' ? undefined : describedBy}
        aria-invalid={hasError}
        className={`${styles.input} ${hasError ? styles.inputError : ''}`}
        id={id}
      />

      {hasError && (
        <p className={styles.error} id={errorId} role="alert">
          {/* Ícone junto do texto: cor não pode ser o único portador de
              significado (WCAG 1.4.1, NFR-96). */}
          <span aria-hidden="true">✕ </span>
          {errors.join(' ')}
        </p>
      )}
    </div>
  );
}
